import clsx from 'clsx';
import { Archive, ArchiveRestore, ArrowLeft, ExternalLink, Pencil, Plus, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import type { PartyRole } from '@pz/core';
import {
  CompanyDialog,
  CompanyStatusPill,
  ContactDialog,
  PartyDialog,
  TermsDialog,
  type ContactBase,
} from '../components/customers';
import { Button, ConfirmModal, EmptyState, ErrorBox, Loading, Notice, PageHeader, Panel, Pill, th, td, useToast } from '../components/ui';
import { ScrollBox } from '../components/virtual';
import { d, hh, money } from '../lib/format';
import { useM, useQ } from '../lib/hooks';
import { useMe } from '../lib/me';

function Row({ label, children }: { label: string; children: ReactNode }) {
  if (children === null || children === undefined || children === '' || children === false) return null;
  return (
    <div className="grid grid-cols-[8.5rem_1fr] gap-2 border-b border-line/60 py-1.5 text-sm last:border-0">
      <dt className="text-graphite">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </div>
  );
}

/**
 * One company: master data, contact persons, the projects it takes part in (with its role and the payment terms of
 * the project) and the documents of these projects (read only). Only for the right "Kundenstamm".
 */
export function CompanyPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const toast = useToast();
  const { can } = useMe();
  const allowed = can('customers.manage');
  const q = useQ('customers.company', { id }, { enabled: allowed });
  const list = useQ('customers.companies', {}, { enabled: allowed });
  const [editing, setEditing] = useState(false);
  const [contact, setContact] = useState<ContactBase | 'new' | null>(null);
  const [party, setParty] = useState(false);
  const [terms, setTerms] = useState<string | null>(null);
  const [removeRole, setRemoveRole] = useState<{ id: string; label: string } | null>(null);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const archive = useM('customers.archiveCompany', { invalidate: ['customers.'] });
  const remove = useM('customers.removePartyRole', { invalidate: ['customers.'] });

  if (!allowed) return <EmptyState>{t('customers.noAccess')}</EmptyState>;
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={q.error} />;
  const { company: c, contacts, projects, documents, seeDocs } = q.data;
  const owners = list.data?.owners ?? [];
  const address = [c.street, [c.postalCode, c.city].filter(Boolean).join(' '), c.country].filter(Boolean);
  const showHours = projects.some((p) => p.hours12m !== null);
  const showWip = projects.some((p) => p.wipCents !== null);

  const termsPill = (p: (typeof projects)[number]) => {
    if (!p.hasTerms) return <Pill tone="neutral">{t('customers.terms.none')}</Pill>;
    if (!p.gaps || p.gaps.length === 0) return <Pill tone="good">{t('customers.terms.complete')}</Pill>;
    return (
      <Pill tone="warn" title={p.gaps.map((g) => t(`customers.gaps.${g}`)).join(', ')}>
        {t('customers.terms.gapsCount', { count: p.gaps.length })}
      </Pill>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Link to="/customers" className="inline-flex items-center gap-1 text-sm text-accent hover:underline">
          <ArrowLeft className="size-3.5" /> {t('customers.detail.back')}
        </Link>
      </div>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-2">
            {c.name}
            <CompanyStatusPill status={c.status} />
            {c.archived && <Pill>{t('customers.archivedPill')}</Pill>}
          </span>
        }
        subtitle={c.legalName ?? undefined}
        actions={
          <>
            <Button icon={<Pencil className="size-4" />} onClick={() => setEditing(true)}>
              {t('common.edit')}
            </Button>
            <Button
              icon={c.archived ? <ArchiveRestore className="size-4" /> : <Archive className="size-4" />}
              loading={archive.isPending}
              onClick={() => (c.archived ? archive.mutate({ id: c.id, archived: false }, { onSuccess: () => toast.ok(t('common.saved')) }) : setConfirmArchive(true))}
            >
              {c.archived ? t('customers.detail.unarchive') : t('customers.detail.archive')}
            </Button>
          </>
        }
      />
      {c.status === 'do_not_contact' && <Notice tone="bad">{t('customers.dncHint')}</Notice>}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,24rem)_minmax(0,1fr)]">
        <Panel title={t('customers.detail.master')}>
          <dl>
            <Row label={t('customers.detail.address')}>
              {address.length > 0 && (
                <span className="block">
                  {address.map((line, i) => (
                    <span key={i} className="block">
                      {line}
                    </span>
                  ))}
                </span>
              )}
            </Row>
            <Row label={t('customers.company.type')}>{t(`customers.types.${c.type}`)}</Row>
            <Row label={t('customers.company.vatId')}>
              {c.vatId && (
                <span className="flex flex-wrap items-center gap-2">
                  {c.vatId}
                  {c.taxCheck.status === 'ok' && <Pill tone="good">{t('customers.company.taxOk')}</Pill>}
                  {c.taxCheck.status === 'bad' && <Pill tone="warn">{t('customers.company.taxBad')}</Pill>}
                </span>
              )}
            </Row>
            <Row label={t('customers.company.leitwegId')}>{c.leitwegId}</Row>
            <Row label={t('customers.company.customerNo')}>{c.customerNo}</Row>
            <Row label={t('customers.company.debitorNo')}>{c.debitorNo}</Row>
            <Row label={t('customers.company.owner')}>{owners.find((o) => o.id === c.ownerId)?.name}</Row>
            <Row label={t('customers.company.note')}>
              <span className="whitespace-pre-line">{c.note}</span>
            </Row>
          </dl>
        </Panel>

        <div className="flex min-w-0 flex-col gap-4">
          <Panel
            title={t('customers.detail.contacts')}
            bodyClassName="p-0"
            actions={
              <Button size="sm" icon={<Plus className="size-3.5" />} onClick={() => setContact('new')}>
                {t('customers.detail.addContact')}
              </Button>
            }
          >
            {contacts.length === 0 ? (
              <p className="p-4 text-sm text-graphite">{t('customers.detail.noContacts')}</p>
            ) : (
              <ScrollBox maxHeight="min(40vh, 22rem)">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 border-b border-line bg-sheet-2">
                    <tr>
                      <th className={th}>{t('customers.cols.contact')}</th>
                      <th className={th}>{t('customers.cols.email')}</th>
                      <th className={th}>{t('customers.cols.phone')}</th>
                      <th className={th}>{t('customers.cols.roles')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map((k) => (
                      <tr
                        key={k.id}
                        tabIndex={0}
                        className={clsx('cursor-pointer border-b border-line/60 last:border-0 hover:bg-sheet-2 focus-visible:bg-sheet-2 focus-visible:outline-none', !k.active && 'opacity-60')}
                        onClick={() => setContact(k)}
                        onKeyDown={(e) => e.key === 'Enter' && setContact(k)}
                      >
                        <td className={td}>
                          <span className="block font-medium">{[k.firstName, k.lastName].filter(Boolean).join(' ')}</span>
                          <span className="block text-xs text-graphite">{k.position}</span>
                          {!k.active && <Pill className="mt-0.5">{t('customers.inactivePill')}</Pill>}
                        </td>
                        <td className={td}>
                          {k.email &&
                            (c.status === 'do_not_contact' ? (
                              k.email
                            ) : (
                              <a className="text-accent hover:underline" href={`mailto:${k.email}`} onClick={(e) => e.stopPropagation()}>
                                {k.email}
                              </a>
                            ))}
                        </td>
                        <td className={td}>{k.phone}</td>
                        <td className={td}>
                          <span className="flex flex-wrap gap-1">
                            {k.roles.map((r) => (
                              <Pill key={r}>{t(`customers.contactRoles.${r as 'invoice'}`)}</Pill>
                            ))}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollBox>
            )}
          </Panel>

          <Panel
            title={t('customers.detail.projects')}
            bodyClassName="p-0"
            actions={
              <Button size="sm" icon={<Plus className="size-3.5" />} onClick={() => setParty(true)}>
                {t('customers.party.add')}
              </Button>
            }
          >
            {projects.length === 0 ? (
              <p className="p-4 text-sm text-graphite">{t('customers.party.none')}</p>
            ) : (
              <ScrollBox maxHeight="min(52vh, 30rem)">
                <ul className="divide-y divide-line/70">
                  {projects.map((p) => (
                    <li key={p.id} className="grid gap-x-4 gap-y-1 px-4 py-2.5 md:grid-cols-[minmax(0,1fr)_auto]">
                      <div className="min-w-0">
                        <Link to={`/projects/${p.id}`} className="inline-flex max-w-full items-center gap-1 font-medium hover:text-accent hover:underline">
                          <span className="truncate">
                            {p.number} {p.name}
                          </span>
                          <ExternalLink className="size-3 shrink-0 text-faint" aria-hidden />
                        </Link>
                        <div className="mt-1 flex flex-wrap items-center gap-1.5">
                          {p.roles.map((r) => (
                            <span key={r.id} className="inline-flex items-center gap-0.5 rounded-full border border-transparent bg-accent-soft py-px pr-0.5 pl-2 text-xs font-medium text-accent">
                              {t(`customers.roles.${r.role as PartyRole}`)}
                              {r.contactName ? ` · ${r.contactName}` : ''}
                              <button
                                type="button"
                                className="rounded-full p-0.5 hover:bg-accent/15"
                                aria-label={t('customers.party.remove')}
                                title={t('customers.party.remove')}
                                onClick={() => setRemoveRole({ id: r.id, label: `${p.number} · ${t(`customers.roles.${r.role as PartyRole}`)}` })}
                              >
                                <X className="size-3" />
                              </button>
                            </span>
                          ))}
                          {p.archived && <Pill>{t('customers.archivedPill')}</Pill>}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 md:justify-end">
                        {showHours && p.hours12m !== null && (
                          <span className="num text-xs text-graphite" title={t('customers.cols.hours12m')}>
                            {hh(p.hours12m, 0)}
                          </span>
                        )}
                        {showWip && p.wipCents !== null && p.wipCents > 0 && (
                          <span className="num text-xs text-graphite" title={t('customers.cols.wip')}>
                            {money(p.wipCents)}
                          </span>
                        )}
                        {termsPill(p)}
                        <Button size="sm" onClick={() => setTerms(p.id)}>
                          {t('customers.detail.terms')}
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              </ScrollBox>
            )}
          </Panel>

          {seeDocs && (
            <Panel title={t('customers.docs.title')} bodyClassName="p-0">
              <p className="px-4 pt-3 text-xs text-graphite">{t('customers.docs.hint')}</p>
              {documents.length === 0 ? (
                <p className="p-4 text-sm text-graphite">{t('customers.docs.none')}</p>
              ) : (
                <ScrollBox maxHeight="min(40vh, 22rem)">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 border-b border-line bg-sheet-2">
                      <tr>
                        <th className={th}>{t('customers.cols.number')}</th>
                        <th className={th}>{t('customers.cols.kind')}</th>
                        <th className={th}>{t('customers.cols.project')}</th>
                        <th className={th}>{t('customers.cols.period')}</th>
                        <th className={th}>{t('customers.cols.status')}</th>
                        <th className={`${th} text-right`}>{t('customers.cols.amount')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {documents.map((x) => (
                        <tr key={x.id} className="border-b border-line/60 last:border-0 hover:bg-sheet-2">
                          <td className={td}>
                            <Link to={`/billing/${x.id}`} className="text-accent hover:underline">
                              {x.number ?? t('customers.docs.draft')}
                            </Link>
                          </td>
                          <td className={td}>{t(`billing.kinds.${x.kind as 'stundennachweis'}`)}</td>
                          <td className={td}>{x.projectNumber}</td>
                          <td className={`${td} num`}>{x.from && x.to ? `${d(x.from)} – ${d(x.to)}` : ''}</td>
                          <td className={td}>{t(`billing.docStatus.${x.status as 'draft'}`)}</td>
                          <td className={`${td} num text-right`}>{money(x.totalCents)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollBox>
              )}
            </Panel>
          )}
        </div>
      </div>

      <CompanyDialog open={editing} company={c} owners={owners} onClose={() => setEditing(false)} />
      <ContactDialog open={contact !== null} contact={contact === 'new' ? null : contact} defaultCompanyId={c.id} onClose={() => setContact(null)} />
      {party && <PartyDialog companyId={c.id} onClose={() => setParty(false)} />}
      {terms && <TermsDialog projectId={terms} onClose={() => setTerms(null)} />}
      <ConfirmModal
        open={confirmArchive}
        title={t('customers.detail.archiveTitle')}
        text={t('customers.detail.archiveText')}
        confirmLabel={t('customers.detail.archive')}
        loading={archive.isPending}
        error={archive.error}
        onClose={() => setConfirmArchive(false)}
        onConfirm={() =>
          archive.mutate(
            { id: c.id, archived: true },
            {
              onSuccess: () => {
                setConfirmArchive(false);
                toast.ok(t('common.saved'));
              },
            },
          )
        }
      />
      <ConfirmModal
        open={removeRole !== null}
        danger
        title={t('customers.party.removeTitle')}
        text={t('customers.party.removeText')}
        confirmLabel={t('customers.party.remove')}
        loading={remove.isPending}
        error={remove.error}
        onClose={() => setRemoveRole(null)}
        onConfirm={() =>
          removeRole &&
          remove.mutate(
            { id: removeRole.id },
            {
              onSuccess: () => {
                setRemoveRole(null);
                toast.ok(t('common.saved'));
              },
            },
          )
        }
      />
    </div>
  );
}
